import React, { useEffect, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { Plus, Circle, CircleDot, CheckCircle2, Pencil, Trash2 } from "lucide-react";
import { api } from "@/lib/api";
import { usePanel } from "@/context/PanelContext";
import { toast } from "sonner";
import EditTaskModal from "@/components/EditTaskModal";
import CompanyBadge from "@/components/CompanyBadge";
import { useConfirm } from "@/components/ConfirmDialog";

const STATUS_META = {
  todo: { icon: Circle, label: "To do", pill: "bg-slate-100 text-slate-700 border-slate-200" },
  doing: { icon: CircleDot, label: "In progress", pill: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  done: { icon: CheckCircle2, label: "Done", pill: "bg-emerald-50 text-emerald-700 border-emerald-200" },
};

export default function TasksPage() {
  const { openNew } = useOutletContext();
  const { open: openPanel, refreshKey, triggerRefresh } = usePanel();
  const confirm = useConfirm();
  const [tasks, setTasks] = useState([]);
  const [filter, setFilter] = useState("all");
  const [editing, setEditing] = useState(null);

  const load = () => api.get("/tasks").then((r) => setTasks(r.data));
  useEffect(() => { load(); }, [refreshKey]);

  const filtered = tasks.filter((t) => filter === "all" ? true : t.status === filter);

  const removeTask = async (e, task) => {
    e.stopPropagation();
    const ok = await confirm({
      title: "Delete task?",
      description: `"${task.title}" will be permanently removed.`,
      confirmText: "Delete",
      danger: true,
    });
    if (!ok) return;
    try {
      await api.delete(`/tasks/${task.id}`);
      toast.success("Task deleted");
      triggerRefresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Delete failed");
    }
  };

  return (
    <div className="px-6 md:px-10 pt-6 max-w-5xl mx-auto" data-testid="tasks-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-display text-3xl font-extrabold text-slate-900">Tasks</h1>
          <p className="text-slate-500 mt-1">{tasks.length} total · {tasks.filter((t) => t.status !== "done").length} open</p>
        </div>
        <button onClick={openNew} className="tg-btn-primary" data-testid="tasks-new">
          <Plus className="h-4 w-4" /> New task
        </button>
      </div>

      <div className="flex gap-2 mb-4">
        {["all", "todo", "doing", "done"].map((f) => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            className={`tg-pill ${filter === f ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-white text-slate-500 border-slate-200 hover:bg-slate-50"}`}
            data-testid={`filter-${f}`}
          >
            {f === "all" ? "All" : STATUS_META[f].label}
          </button>
        ))}
      </div>

      <div className="tg-card divide-y divide-slate-100">
        {filtered.length === 0 && (
          <div className="p-8 text-center text-slate-400 text-sm">No tasks yet. Type in the AI bar — “Create a task to…”.</div>
        )}
        {filtered.map((t, i) => {
          const meta = STATUS_META[t.status] || STATUS_META.todo;
          const Icon = meta.icon;
          return (
            <div
              key={t.id}
              className="group w-full flex items-center gap-3 px-5 py-4 hover:bg-slate-50 transition-colors first:rounded-t-2xl last:rounded-b-2xl"
              data-testid={`task-row-${i}`}
            >
              <button
                onClick={() => openPanel("task", t)}
                className="flex items-center gap-3 flex-1 min-w-0 text-left"
                data-testid={`task-open-${i}`}
              >
                <Icon className={`h-4 w-4 ${t.status === "done" ? "text-emerald-500" : t.status === "doing" ? "text-emerald-500" : "text-slate-400"}`} />
                <div className="flex-1 min-w-0">
                  <div className={`text-sm font-semibold ${t.status === "done" ? "text-slate-400 line-through" : "text-slate-800"} truncate`}>{t.title}</div>
                  {t.description && <div className="text-xs text-slate-500 truncate mt-0.5">{t.description}</div>}
                </div>
                <CompanyBadge entry={t} />
                <span className={`tg-pill ${meta.pill}`}>{meta.label}</span>
              </button>
              <button
                onClick={(e) => { e.stopPropagation(); setEditing(t); }}
                className="opacity-0 group-hover:opacity-100 transition-opacity h-8 w-8 rounded-lg hover:bg-emerald-50 text-slate-400 hover:text-emerald-600 flex items-center justify-center"
                title="Edit task"
                data-testid={`task-edit-${i}`}
              >
                <Pencil className="h-4 w-4" />
              </button>
              <button
                onClick={(e) => removeTask(e, t)}
                className="opacity-0 group-hover:opacity-100 transition-opacity h-8 w-8 rounded-lg hover:bg-rose-50 text-slate-400 hover:text-rose-600 flex items-center justify-center"
                title="Delete task"
                data-testid={`task-delete-${i}`}
              >
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          );
        })}
      </div>

      <EditTaskModal
        open={!!editing}
        onOpenChange={(o) => { if (!o) setEditing(null); }}
        task={editing}
        onSaved={triggerRefresh}
      />
    </div>
  );
}
